import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth/AuthPages.css';

/**
 * ResetPasswordPage - Set new password using reset token
 */
export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { resetPassword, isLoggingIn, error, clearError } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  // Show error if no token
  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Lien invalide</h2>
          </header>

          <div className="auth-alert error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Token manquant</p>
              <p className="auth-alert-message">
                Le lien de réinitialisation est invalide ou a expiré.
                Veuillez demander un nouveau lien.
              </p>
            </div>
          </div>

          <div className="auth-links">
            <Link to="/forgot-password" className="auth-link">
              Demander un nouveau lien
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!password) {
      errors.password = 'Le mot de passe est requis';
    } else if (password.length < 8) {
      errors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) return;

    const result = await resetPassword({ token, password });

    if (result.success) {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Mot de passe modifié</h2>
          </header>

          <div className="auth-alert success" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Succès !</p>
              <p className="auth-alert-message">
                Votre mot de passe a été modifié avec succès.
                Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
            </div>
          </div>

          <div className="auth-links">
            <Link to="/login" className="auth-link">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-box">
        <header className="auth-header">
          <h1 className="auth-logo">Corpus</h1>
          <h2 className="auth-title">Nouveau mot de passe</h2>
          <p className="auth-subtitle">
            Choisissez un nouveau mot de passe pour votre compte.
          </p>
        </header>

        {error && (
          <div className="auth-alert error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Erreur</p>
              <p className="auth-alert-message">{error}</p>
            </div>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label className="auth-label" htmlFor="password">Nouveau mot de passe</label>
            <div className="auth-password-field">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`auth-input ${fieldErrors.password ? 'error' : ''}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Au moins 8 caractères"
                autoComplete="new-password"
                disabled={isLoggingIn}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="auth-field-error">{fieldErrors.password}</p>
            )}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="confirmPassword">Confirmer le mot de passe</label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              className={`auth-input ${fieldErrors.confirmPassword ? 'error' : ''}`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Répétez le mot de passe"
              autoComplete="new-password"
              disabled={isLoggingIn}
            />
            {fieldErrors.confirmPassword && (
              <p className="auth-field-error">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          <button type="submit" className="auth-submit" disabled={isLoggingIn}>
            {isLoggingIn ? (
              <>
                <span className="auth-spinner" />
                Modification...
              </>
            ) : (
              'Modifier le mot de passe'
            )}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login" className="auth-link">
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};
